// Feature: address-geolocation, Tasks 8.2 + 8.3 + 8.4
//
// <MapComponent> — composes the AddressForm postal-address section with a
// distinct geographic-location section per Req 12.9. The two concepts are
// never conflated: Postal Address is always the top card, Geographic
// Location is always the bottom card, both wrapped in the same shell.
//
// Rendering contract (Req 8.1–8.4, 12.2–12.8):
//   - The location section reads only from `location.latitude`/
//     `location.longitude` — no other stored field participates in
//     rendering (P-ProviderSwap).
//   - An optional `mappingProvider` prop lets a caller inject a Leaflet /
//     MapLibre / static-tile adapter. When omitted, the section renders a
//     Coordinate-Preview card containing lat/lng, the four controls, and a
//     manual-edit inline form. Leaflet integration is Task 4.2 — deferred
//     until a spec calls for interactive map editing.
//   - `onCoordinateEdit` is the explicit-edit callback (Req 4.1). The
//     parent decides whether to persist. Pin-drag from an injected
//     `MappingProvider.onPinDrag` calls the same handler (Req 12.3).
//   - The four location controls (Req 12.5–12.8) are always rendered when
//     a location is present: Copy Coordinates, Copy What3Words (only when
//     `identifiers.what3words` present, Req 12.6), Open in Maps (external
//     nav app URL, Req 12.7), Full Screen (toggle, Req 12.8).

import React, { useEffect, useRef, useState } from "react";
import type {
  Address,
  GeoLocation,
  LocationIdentifiers,
  MappingProvider,
} from "@akfdev-com/shared";
import AddressForm, {
  type AddressFormErrors,
  type AddressFormProps,
  type PartialAddress,
} from "./AddressForm";

export interface CoordinateEdit {
  latitude: number;
  longitude: number;
  /**
   * How the edit was triggered — passed through so the parent can call
   * `applyLocationUpdate` with the right trigger kind (Req 4.1 vs 12.3).
   */
  source: "manual-edit" | "pin-drag";
}

export interface MapComponentProps {
  /** Current postal address state, forwarded to `<AddressForm>`. */
  address: PartialAddress;
  onAddressChange: (next: PartialAddress) => void;
  addressErrors?: AddressFormErrors;

  /** Current stored physical location, or `undefined` when unset. */
  location?: GeoLocation;
  identifiers?: LocationIdentifiers;

  /**
   * Called when the user makes an explicit coordinate edit (manual entry
   * or pin drag). Parent runs it through `applyLocationUpdate` and
   * persists (Req 4.1 / 12.3).
   */
  onCoordinateEdit?: (edit: CoordinateEdit) => void;

  /**
   * Optional map-rendering provider. When present, the location section
   * mounts a `<div>` element and hands it to `provider.render(...)`.
   * Pin-drag events are wired to `onCoordinateEdit` via
   * `provider.onPinDrag`.
   */
  mappingProvider?: MappingProvider;

  disabled?: boolean;
  idPrefix?: string;
}

const cardClass =
  "bg-white shadow rounded-lg p-4 sm:p-6 border border-gray-200";
const sectionTitleClass =
  "text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide";
const controlButtonClass =
  "inline-flex items-center justify-center px-3 py-1.5 min-h-[36px] text-xs " +
  "rounded-md bg-blue-50 text-blue-800 border border-blue-200 " +
  "hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 " +
  "disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

function fixed(n: number, digits = 6): string {
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(digits);
}

function externalMapsUrl(lat: number, lng: number): string {
  // OSM universal deep link — works in every browser without a vendor key.
  // Native mobile apps that register `geo:` intents will pick up the URI
  // via the `geo:` fallback below; for a plain <a href>, OSM is safest.
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}`;
}

async function safeCopy(text: string): Promise<boolean> {
  try {
    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function"
    ) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through
  }
  return false;
}

const MapComponent: React.FC<MapComponentProps> = ({
  address,
  onAddressChange,
  addressErrors,
  location,
  identifiers,
  onCoordinateEdit,
  mappingProvider,
  disabled = false,
  idPrefix = "map",
}) => {
  const [manualEditOpen, setManualEditOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [copiedFlash, setCopiedFlash] = useState<string | null>(null);
  const [manualLat, setManualLat] = useState<string>("");
  const [manualLng, setManualLng] = useState<string>("");
  const [manualError, setManualError] = useState<string | null>(null);
  const mapMountRef = useRef<HTMLDivElement | null>(null);

  const lat = location?.latitude;
  const lng = location?.longitude;
  const hasLocation = typeof lat === "number" && typeof lng === "number";
  const w3w = identifiers?.what3words;

  useEffect(() => {
    if (!mappingProvider) return;
    if (!mapMountRef.current) return;
    if (!hasLocation) return;
    mappingProvider.render(mapMountRef.current, lat!, lng!);
    if (onCoordinateEdit) {
      mappingProvider.onPinDrag((newLat, newLng) => {
        onCoordinateEdit({
          latitude: newLat,
          longitude: newLng,
          source: "pin-drag",
        });
      });
    }
    // Intentional: re-run when the coordinate values change so the map
    // repositions immediately (Req 12.4).
  }, [mappingProvider, hasLocation, lat, lng, onCoordinateEdit]);

  const showCopiedFlash = (label: string): void => {
    setCopiedFlash(label);
    window.setTimeout(() => setCopiedFlash(null), 1500);
  };

  const onCopyCoords = async (): Promise<void> => {
    if (!hasLocation) return;
    const text = `${lat!.toFixed(6)}, ${lng!.toFixed(6)}`;
    const ok = await safeCopy(text);
    if (ok) showCopiedFlash("Coordinates copied");
  };

  const onCopyW3w = async (): Promise<void> => {
    if (!w3w) return;
    const ok = await safeCopy(w3w);
    if (ok) showCopiedFlash("What3Words copied");
  };

  const onManualSubmit = (): void => {
    setManualError(null);
    const parsedLat = Number(manualLat);
    const parsedLng = Number(manualLng);
    if (!Number.isFinite(parsedLat) || !Number.isFinite(parsedLng)) {
      setManualError("Latitude and longitude must be numbers");
      return;
    }
    if (parsedLat < -90 || parsedLat > 90) {
      setManualError("Latitude must be between -90 and 90");
      return;
    }
    if (parsedLng < -180 || parsedLng > 180) {
      setManualError("Longitude must be between -180 and 180");
      return;
    }
    if (onCoordinateEdit) {
      onCoordinateEdit({
        latitude: parsedLat,
        longitude: parsedLng,
        source: "manual-edit",
      });
    }
    setManualEditOpen(false);
  };

  const openManualEdit = (): void => {
    setManualLat(hasLocation ? String(lat) : "");
    setManualLng(hasLocation ? String(lng) : "");
    setManualError(null);
    setManualEditOpen(true);
  };

  const wrapperClass = fullscreen
    ? "fixed inset-0 z-50 bg-white overflow-auto p-4 sm:p-6 space-y-4"
    : "space-y-4";

  return (
    <div className={wrapperClass} data-testid="map-component">
      {/* Postal Address section — Req 12.9 (distinct section) */}
      <section className={cardClass} aria-labelledby={`${idPrefix}-postal-h`}>
        <h3 id={`${idPrefix}-postal-h`} className={sectionTitleClass}>
          Postal Address
        </h3>
        <AddressForm
          value={address}
          onChange={onAddressChange}
          errors={addressErrors}
          disabled={disabled}
          idPrefix={`${idPrefix}-address`}
        />
      </section>

      {/* Geographic Location section — Req 12.9 */}
      <section
        className={cardClass}
        aria-labelledby={`${idPrefix}-geo-h`}
        data-testid="map-location-section"
      >
        <h3 id={`${idPrefix}-geo-h`} className={sectionTitleClass}>
          Geographic Location
        </h3>

        {/* Coordinates readout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">
              Latitude
            </p>
            <p
              className="text-sm text-gray-700 font-mono"
              data-testid="map-lat"
            >
              {hasLocation ? fixed(lat!) : "—"}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">
              Longitude
            </p>
            <p
              className="text-sm text-gray-700 font-mono"
              data-testid="map-lng"
            >
              {hasLocation ? fixed(lng!) : "—"}
            </p>
          </div>
        </div>

        {/* Map area — either the injected provider mounts here, or we show a
            coord-preview placeholder. Rendering depends ONLY on lat/lng
            (Req 8.2 / P-ProviderSwap). */}
        <div
          ref={mapMountRef}
          role="region"
          aria-label="Map preview"
          data-testid="map-mount"
          className={
            fullscreen
              ? "h-[70vh] w-full rounded-md border border-gray-300 bg-gray-100 flex items-center justify-center"
              : "h-48 w-full rounded-md border border-gray-300 bg-gray-100 flex items-center justify-center"
          }
        >
          {!mappingProvider && (
            <p className="text-xs text-gray-500 px-4 text-center">
              {hasLocation
                ? "Map preview: configure a MappingProvider to render an interactive map. Coordinates above are canonical."
                : "No location set. Use “Set coordinates” or geocode the postal address to add a location."}
            </p>
          )}
        </div>

        {/* Controls row — Req 12.5 / 12.6 / 12.7 / 12.8 */}
        <div className="mt-4 flex flex-wrap gap-2 items-center">
          <button
            type="button"
            className={controlButtonClass}
            onClick={onCopyCoords}
            disabled={!hasLocation || disabled}
            data-testid="map-copy-coords"
            aria-label="Copy coordinates to clipboard"
          >
            Copy coordinates
          </button>

          {w3w ? (
            <button
              type="button"
              className={controlButtonClass}
              onClick={onCopyW3w}
              disabled={disabled}
              data-testid="map-copy-w3w"
              aria-label={`Copy What3Words ${w3w} to clipboard`}
            >
              Copy What3Words
            </button>
          ) : null}

          <a
            className={
              hasLocation
                ? controlButtonClass
                : controlButtonClass + " pointer-events-none opacity-50"
            }
            href={hasLocation ? externalMapsUrl(lat!, lng!) : "#"}
            target="_blank"
            rel="noopener noreferrer"
            aria-disabled={!hasLocation}
            data-testid="map-open-external"
            aria-label="Open in external maps app"
          >
            Open in maps
          </a>

          <button
            type="button"
            className={controlButtonClass}
            onClick={() => setFullscreen((f) => !f)}
            data-testid="map-fullscreen"
            aria-label={fullscreen ? "Exit full screen" : "Enter full screen"}
            aria-pressed={fullscreen}
          >
            {fullscreen ? "Exit full screen" : "Full screen"}
          </button>

          <button
            type="button"
            className={controlButtonClass}
            onClick={openManualEdit}
            disabled={disabled || !onCoordinateEdit}
            data-testid="map-manual-edit"
          >
            Set coordinates
          </button>

          {copiedFlash ? (
            <span
              className="text-xs text-green-700 ml-1"
              role="status"
              aria-live="polite"
              data-testid="map-copied-flash"
            >
              {copiedFlash}
            </span>
          ) : null}
        </div>

        {/* Manual edit inline form (Req 4.1 — explicit coordinate edit) */}
        {manualEditOpen ? (
          <div
            className="mt-4 border-t border-gray-200 pt-4"
            data-testid="map-manual-edit-form"
          >
            <p className="text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">
              Manual coordinate edit
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-gray-500">Latitude</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm mt-1"
                  value={manualLat}
                  onChange={(e) => setManualLat(e.target.value)}
                  data-testid="map-manual-lat"
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500">Longitude</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm mt-1"
                  value={manualLng}
                  onChange={(e) => setManualLng(e.target.value)}
                  data-testid="map-manual-lng"
                />
              </label>
            </div>
            {manualError ? (
              <p
                className="text-red-600 text-xs mt-2"
                data-testid="map-manual-error"
              >
                {manualError}
              </p>
            ) : null}
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                className={controlButtonClass}
                onClick={onManualSubmit}
                data-testid="map-manual-save"
              >
                Save coordinates
              </button>
              <button
                type="button"
                className={controlButtonClass}
                onClick={() => {
                  setManualEditOpen(false);
                  setManualError(null);
                }}
                data-testid="map-manual-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default MapComponent;

// Re-export the small forwarding types so consumers don't have to import
// them from two places.
export type { AddressFormProps, AddressFormErrors, PartialAddress };
export type { Address, GeoLocation, LocationIdentifiers, MappingProvider };
